import classNames from "classnames";
import { ButtonSecondary } from "components/Atoms/Button";
import { Panel } from "components/panels";
import { MySelectionTable } from "components/Tables";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import { ConfigureExport } from "./ConfigureExport";
import { appendQueryString } from "utilities/queryString";
import { useLoaderData } from "react-router";

const InvoiceExportApp = () => {
  const viewModel = useLoaderData<server.viewmodel.InvoiceExportListFrontModel>();

  const perPage = 30;

  const [bookkeeping, setBookkeeping] = useState<server.dto.BookkeepingSmall>(viewModel.initialSelectedBookkeeping);
  const [configuration, setConfiguration] = useState<server.dto.InvoiceExportConfig>(viewModel.invoiceExportConfiguration);
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState<boolean>(false);
  const [instruction, setInstruction] = useState<server.dto.PaginatedInvoiceExportInstruction>({
    bookkeepingId: bookkeeping.bookkeepingId,
    page: 1,
    perPage,
  });

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, perPage, paginate);
  const isPaginationVisible = pagination.pageCount > 1;

  const onBookkeepingChange = (b: server.dto.BookkeepingSmall) => {
    setBookkeeping(b);
    setInstruction(prev => ({ ...prev, bookkeepingId: b.bookkeepingId }));
  }

  const onConfigurationChange = (c: server.dto.InvoiceExportConfig) => {
    setConfiguration(c);
  }

  useEffect(() => {
    const fetchConfiguration = async () => {
      setIsLoadingConfiguration(true);
      const response = await window.CoreApi.InvoiceExport.GetExportConfiguration({ bookkeepingId: bookkeeping.bookkeepingId });
      setConfiguration(response.data?.data);
      setIsLoadingConfiguration(false);
    }
    appendQueryString({
      bookkeepingId: `${bookkeeping.bookkeepingId}`
    });
    fetchConfiguration();
  }, [bookkeeping.bookkeepingId]);

  const api = useCustomQuery({
    queryFn: async () => {
      return await window.CoreApi.InvoiceExport.Paginated(instruction);
    },
    queryKey: [instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.data.pagination);
    },
    cacheTime: 0,
  });
  const data = api.data?.data.data ?? [];
  const isLoading = api.isLoading;

  return <Panel
    headerContainerClasses="top-navigation-wrapper"
    contentCssClass="flex-column panel-gap"
  >
    <ConfigureExport
      configuration={configuration}
      isLoadingConfiguration={isLoadingConfiguration}
      onConfigurationChange={onConfigurationChange}
      availableBookkeepings={viewModel.availableBookkeepings}
      bookkeeping={bookkeeping}
      onBookkeepingChange={onBookkeepingChange}
    />

    <div className="s1_panel">
      <MySelectionTable
        items={data}
        isLoading={isLoading}
        hideHeader
        columns={[{
          key: "key",
          header: "",
          render: (i) => <div>
            {/* The server gives us a 1-indexed month. Moment expects a 0-indexed month, so we subtract one */}
            {moment.utc().year(i.year).month(i.month - 1).format("YYYY MM")}
          </div>,
          size: "1fr"
        }, {
          key: "export",
          header: "",
          render: (i) => <div>
            {configuration.exportMask.availableFileTypes.map(availableFileType =>
              <a
                style={{ display: "block" }}
                key={availableFileType.type}
                href={window.CoreApi.InvoiceExport.Export.absoluteUrl(bookkeeping.bookkeepingId, i.year, i.month, availableFileType.type)}
              >
                {availableFileType.label}
              </a>
            )}
          </div>,
          size: "3fr",
        }, {
          key: "lock",
          header: "",
          render: (i) => <div>
            {!i.allAreLocked && <ButtonSecondary
              label={RESX.InvoiceExport.LockInvoices}
              onClick={async () => {
                await window.CoreApi.InvoiceExport.LockPerMonth({
                  type: "unknown",
                  month: i.month,
                  year: i.year,
                  bookkeepingId: bookkeeping.bookkeepingId,
                })
                api.refetch();
              }}
            />}
            <div className={classNames({
              "s1_red": !i.allAreLocked,
            })}>
              {i.lockedInvoiceCount} / {i.totalInvoiceCount} {RESX.InvoiceExport.Locked}
            </div>
          </div>
        }]}
        getKey={(i) => `${i.year}-${i.month}`}
        pagination={isPaginationVisible && pagination.Component}
        title={`${viewModel.pageTitle}: ${pagination.total}`}
        canEdit={() => false}
        canDelete={() => false}
      />
    </div>
  </Panel>
}

export default InvoiceExportApp;
